<template>
  <div>
    <v-treeview
      selected-color="yellow"
      v-model="selectedTree"
      :items="roleTree"
      item-key="uid"
      selectable
      dense
      selection-type="leaf"
      :return-object="true"
      open-on-click>
      <template v-slot:prepend="{ item }">
        <v-icon color="blue-grey lighten-3" v-if="item.children && item.children.length"
          >mdi-folder</v-icon
        >
        <v-icon color="blue-grey lighten-3" v-else>mdi-key</v-icon>
      </template>

      <template v-slot:label="{ item, leaf, attrs, on }">
        <div class="d-flex align-center" v-bind="attrs" v-on="on" @click="onNodeClick(item, leaf)">
          <div class="flex-grow-1">{{ item.name }}</div>
        </div>
      </template>
    </v-treeview>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, watch } from "vue";
import { IRole } from "@/utils/interfaces";

interface RoleTreeNode {
  uid: string;
  id: string | number | null;
  name: string;
  nodeType?: "app" | "role";
  children?: RoleTreeNode[];
}

export default defineComponent({
  name: "RoleTree",
  props: {
    allRoles: { type: Array as PropType<IRole[]>, default: () => [] },
    value: { type: Array as PropType<IRole[]>, default: () => [] },
  },
  emits: ["input"],
  data() {
    return {
      roleTree: [] as RoleTreeNode[],
      selectedTree: [] as RoleTreeNode[],
    };
  },
  methods: {
    updateSelectedFromModel() {
      const model = this.$props.value || [];
      const roleIds = new Set(
        (model as any[]).map((r: any) => String(r && r.id !== undefined ? r.id : r))
      );
      const selected: RoleTreeNode[] = [];
      this.roleTree.forEach((app) => {
        app.children?.forEach((c) => {
          if (roleIds.has(String(c.id))) selected.push(c);
        });
      });
      this.selectedTree = selected;
    },

    buildRoleTree() {
      const allRoles: IRole[] = this.allRoles || [];
      const rolesMap = new Map<string | number | null, RoleTreeNode>();

      allRoles.forEach((role: IRole) => {
        const appKey = role.app_id ?? "auth";
        const existing = rolesMap.get(appKey);

        const child: RoleTreeNode = {
          uid: `role-${role.id}`,
          id: role.id,
          name: role.name,
          nodeType: "role",
        };

        if (existing && existing.children) {
          existing.children.push(child);
        } else {
          rolesMap.set(appKey, {
            uid: `app-${appKey}`,
            id: appKey,
            name: role?.app?.name || "Auth",
            nodeType: "app",
            children: [child],
          });
        }
      });

      this.roleTree = Array.from(rolesMap.values()).sort((a: any, b: any) =>
        String(a.name).localeCompare(String(b.name))
      );

      this.updateSelectedFromModel();
    },

    syncSelectedToModel() {
      if (!this.selectedTree) {
        this.$emit("input", []);
        return;
      }

      const map = new Map<string, IRole>();
      (this.allRoles || []).forEach((r: any) => map.set(String(r.id), r));

      const roles: IRole[] = this.selectedTree
        .filter((n: any) => n.nodeType === "role")
        .map((n: any) => {
          const found = map.get(String(n.id));
          if (found) return { ...found };
          return { id: n.id as number, name: n.name, app_id: null, app: null } as unknown as IRole;
        });

      this.$emit("input", roles);
    },

    onNodeClick(item: RoleTreeNode, leaf: boolean) {
      if (!leaf) return;
      const exists = this.selectedTree.find((n: any) => n.uid === item.uid);
      if (exists) {
        this.selectedTree = this.selectedTree.filter((n: any) => n.uid !== item.uid);
      } else {
        this.selectedTree = [...this.selectedTree, item];
      }
    },
  },
  watch: {
    allRoles: {
      immediate: true,
      handler() {
        this.buildRoleTree();
      },
    },
    value: {
      immediate: true,
      handler() {
        this.updateSelectedFromModel();
      },
    },
    selectedTree: {
      immediate: false,
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
        this.$nextTick(() => {
          this.syncSelectedToModel();
        });
      },
    },
  },
});
</script>

<style scoped></style>
